import { useEffect, useMemo, useCallback, useRef, useState } from 'react'
import { useForm, useFieldArray, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEngagementNavigation } from "@/contexts/engagement-navigation-context"
import { useToast } from "@/hooks/use-toast"
import { useApi } from "@/lib/api"
import { 
  type TaskFormData,
  type TaskFormArrayData,
  createTaskFormArraySchema,
  type WorkerFormData,
  type WorkerFormArrayData,
  createWorkerFormArraySchema,
  type CoreFormData,
  coreFormSchema,
  type BillingSetupFormData,
  createBillingSetupFormSchema,
  type InvoiceFormData,
  createInvoiceFormSchema,
  type TimesheetAndExpenseFormData,
  timesheetAndExpenseFormSchema,
  type MilestoneFormArrayData,
  type MilestoneFormData,
  createMilestoneFormArraySchema,
  type RevenueRecognitionFormData,
  createRevenueRecognitionFormSchema
} from "@/lib/schemas"
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE, ENGAGEMENT_STATUS } from "@/lib/constants/engagement-codes"
import { useEngagementFormValidation } from "@/lib/utils/engagement-form-validation"
import { 
  getInitialCoreData, 
  getInitialBillingData,
  getInitialTimesheetData, 
  getInitialInvoiceData,
  getInitialWorkers,
  getInitialTasks,
  getInitialMilestones,
  createMilestoneWithDefaults
} from "@/lib/constants/engagement-form-defaults"
import { laterOf } from "@/lib/utils"
import type { FormState } from "@/types/ui/engagement"
import type { TaskCoverage } from "@/lib/utils/worker-pay-shape"
import { copyPendingEntries } from "@/lib/utils/scheduled-budgeted-hours"

const DEFAULT_TASK_NAMES = new Set(['Utilized', 'Billable', 'Non-Billable'])

/**
 * The dates a copied worker carries (GW-334).
 *
 * First Day of Work is the later of the worker's start date on the source engagement and the new
 * engagement's start date. A source that already ran would otherwise land every worker before the
 * new term, and each date would have to be corrected by hand before the engagement could be
 * submitted. With no engagement start date to measure against, the source date is no better than a
 * blank one, so the field is left empty for the admin to fill; CoreSection warns before the copy
 * runs in that state, so they can back out and set the start date first.
 *
 * Last Day of Work never copies. A per-worker end date is a decision about the term it was set on,
 * and the case where it would still help (one that falls inside the new term) barely exists, since
 * the new term starts later than the source. Blank means the worker runs to the engagement end date,
 * the same as a newly added worker.
 */
function copiedWorkerDates(
  worker: WorkerFormData,
  engagementStartDate: Date | null
): Pick<WorkerFormData, 'firstDayOfWork' | 'lastDayOfWork'> {
  return {
    firstDayOfWork: engagementStartDate ? laterOf(worker.firstDayOfWork, engagementStartDate) : null,
    lastDayOfWork: null,
  }
}

interface UseEngagementFormsProps {
  formState: FormState | null
  isFormReadonly: boolean
  isSetupMode: boolean
}

export function useEngagementForms({ formState, isFormReadonly, isSetupMode }: UseEngagementFormsProps) {
  // Track if forms have been populated with data
  const [isFormsPopulated, setIsFormsPopulated] = useState(false)
  
  // Get viewed sections from navigation context
  const { viewedSections, unmarkSectionViewed } = useEngagementNavigation()
  const { toast } = useToast()

  // Task auto-management (creating defaults, updating on type/billing change) only runs
  // when the user can still change engagement type and billing type — i.e. setup mode and editable.
  const canAutoManageTasks = isSetupMode && !isFormReadonly
  
  // Determine if accounting fields should be required based on engagement status
  const requireAccountingFields = formState?.actionContext.engagement.status === ENGAGEMENT_STATUS.ACCOUNTING_DRAFT
  
  // Determine if sections require viewing before being marked complete
  // Required when BOTH: 1) Setup mode, AND 2) Draft status (but NOT Accounting Draft)
  const requireViewingForCompletion = isSetupMode && formState?.actionContext.engagement.status === ENGAGEMENT_STATUS.DRAFT
  
  // Pre-create static zodResolvers to prevent recreation on re-renders
  const coreFormResolver = useMemo(() => zodResolver(coreFormSchema), [])
  const timesheetAndExpenseFormResolver = useMemo(() => zodResolver(timesheetAndExpenseFormSchema), [])

  // Create core form
  const coreForm = useForm<CoreFormData>({
    defaultValues: getInitialCoreData(),
    resolver: coreFormResolver,
    mode: 'onChange'
  })

  // OPTIMIZED: Single form.watch call instead of multiple subscriptions
  const coreFormValues = coreForm.watch([
    'startDate',
    'endDate',
    'engagementType',
    'selectedInternalCompany',
    'selectedClient'
  ])

  const [
    startDate,
    endDate,
    engagementType,
    selectedInternalCompany,
    selectedClient
  ] = coreFormValues

  // Create billing setup form with conditional validation based on engagement type
  const billingFormResolver = useMemo(() => {
    return zodResolver(createBillingSetupFormSchema(engagementType, requireAccountingFields))
  }, [engagementType, requireAccountingFields])

  const billingForm = useForm<BillingSetupFormData>({
    defaultValues: getInitialBillingData(),
    resolver: billingFormResolver,
    mode: 'onChange'
  })

  // Trigger validation when billing resolver changes (after resolver is applied)
  useEffect(() => {
    billingForm.trigger()
  }, [billingFormResolver, billingForm])

  // Watch billing form values
  const billingFormValues = billingForm.watch(['billingType', 'totalAmount'])
  const [billingType, totalAmount] = billingFormValues
  
  // Create conditional milestone resolver - only validate when billing type is MILESTONE
  const milestoneFormResolver = useMemo(() => {
    return billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE 
      ? zodResolver(createMilestoneFormArraySchema(requireAccountingFields))
      : undefined // No validation when not milestone billing
  }, [billingType, requireAccountingFields])


  // Create conditional invoice resolver - only validate when engagement type is NOT Internal
  // Also pass billingType for conditional validation (billing frequency not required for milestone)
  const invoiceFormResolver = useMemo(() => {
    return engagementType !== ENGAGEMENT_TYPE.INTERNAL
      ? zodResolver(createInvoiceFormSchema(billingType, requireAccountingFields))
      : undefined // No validation when engagement type is Internal
  }, [engagementType, billingType, requireAccountingFields])
  
  const prevSelectedInternalCompany = useRef<number>(selectedInternalCompany)
  const prevBillingType = useRef<string>(billingType)
  const prevEngagementType = useRef<string>(engagementType)
  const prevBillingTypeForMilestones = useRef<string>(billingType) // Separate ref for milestone creation
  // Track if this is the initial form population to avoid clearing profile fields
  const isInitialFormPopulation = useRef<boolean>(false)


  // Whether the current user may view worker pay rates. Read the same way the display path does
  // (use-engagement-data.ts) so validation and the hidden/redacted pay inputs stay in agreement.
  // NOTE the fallback is intentionally `?? true` here, the OPPOSITE of the adapter's `?? false`:
  // fail-closed for the validation path means "require" (so a missing flag can't let a form save
  // with empty pay fields), whereas fail-closed for the display path means "hide". Don't unify them.
  const canViewWorkerPayRates = formState?.actionContext.canViewWorkerPayRates ?? true

  // Memoize the tasks form resolver
  const tasksFormResolver = useMemo(() => {
    return zodResolver(createTaskFormArraySchema(billingType, engagementType))
  }, [billingType, engagementType])

  // Created ahead of the workers form, whose validation reads what the tasks need.
  const tasksForm = useForm<TaskFormArrayData>({
    defaultValues: { tasks: getInitialTasks() },
    resolver: tasksFormResolver,
    mode: 'onChange'
  })

  // What the engagement's active tasks need a worker to be paid at. Watched, since a task's billability
  // can change while a worker is flagged over it. Held as two flags so the resolver below is rebuilt only
  // when the answer changes, not on every keystroke in a task.
  const watchedTasks = useWatch({ control: tasksForm.control, name: 'tasks', defaultValue: [] })
  const needsBillable = (watchedTasks ?? []).some(task => !task.inactive && task.billable)
  const needsNonBillable = (watchedTasks ?? []).some(task => !task.inactive && !task.billable)
  const taskCoverage = useMemo<TaskCoverage>(
    () => ({ needsBillable, needsNonBillable }),
    [needsBillable, needsNonBillable]
  )

  // Memoize the worker form resolver
  const workerFormResolver = useMemo(() => {
    return zodResolver(createWorkerFormArraySchema(
      billingType,
      engagementType,
      startDate || undefined,
      endDate || undefined,
      canViewWorkerPayRates,
      taskCoverage
    ))
  }, [billingType, engagementType, startDate, endDate, canViewWorkerPayRates, taskCoverage])

  // Create worker form with conditional validation
  const workersForm = useForm<WorkerFormArrayData>({
    defaultValues: { workers: getInitialWorkers() },
    resolver: workerFormResolver,
    mode: 'onChange'
  })

  // Trigger validation when resolver changes (after resolver is applied)
  useEffect(() => {
    workersForm.trigger()
  }, [workerFormResolver, workersForm])

  // Use field array for workers
  const { append: appendWorker, remove: removeWorker, update: updateWorker } = useFieldArray({
    control: workersForm.control,
    name: "workers"
  })

  // Create timesheet form
  const timesheetAndExpenseForm = useForm<TimesheetAndExpenseFormData>({
    defaultValues: getInitialTimesheetData(),
    resolver: timesheetAndExpenseFormResolver,
    mode: 'onChange'
  })

  // Use field array for approvers
  const { append: appendApprover, remove: removeApprover } = useFieldArray({
    control: timesheetAndExpenseForm.control,
    name: "approvers"
  })

  // Use field array for expense approvers
  const { append: appendExpenseApprover, remove: removeExpenseApprover } = useFieldArray({
    control: timesheetAndExpenseForm.control,
    name: "expenseApprovers"
  })

  // Watch approvers for conditional validation (selectedApprovalSequence required when multiple approvers)
  const currentApprovers = useWatch({
    control: timesheetAndExpenseForm.control,
    name: 'approvers',
    defaultValue: []
  })

  // Watch expense approvers for conditional validation
  const currentExpenseApprovers = useWatch({
    control: timesheetAndExpenseForm.control,
    name: 'expenseApprovers',
    defaultValue: []
  })

  // Watch expense methodology for conditional validation
  const expenseMethodologyCode = useWatch({
    control: timesheetAndExpenseForm.control,
    name: 'expenseMethodologyCode'
  })

  // Trigger validation when approver count crosses single/multiple boundary
  useEffect(() => {
    timesheetAndExpenseForm.trigger('selectedApprovalSequence')
  }, [currentApprovers.length, timesheetAndExpenseForm])

  // Trigger validation when expense approvers or methodology changes
  useEffect(() => {
    timesheetAndExpenseForm.trigger('expenseApprovers')
  }, [currentExpenseApprovers.length, expenseMethodologyCode, timesheetAndExpenseForm])

  // Create invoice form
  const invoiceForm = useForm<InvoiceFormData>({
    defaultValues: getInitialInvoiceData(),
    resolver: invoiceFormResolver,
    mode: 'onChange'
  })

  // Trigger validation when invoice resolver changes (after resolver is applied)
  useEffect(() => {
    invoiceForm.trigger()
  }, [invoiceFormResolver, invoiceForm])

  // Use field array for invoice notes
  const { append: appendInvoiceNote, remove: removeInvoiceNote } = useFieldArray({
    control: invoiceForm.control,
    name: "invoiceNotes"
  })

  // Create milestone form with conditional validation
  const milestonesForm = useForm<MilestoneFormArrayData>({
    defaultValues: { milestones: getInitialMilestones() },
    resolver: milestoneFormResolver,
    mode: 'onChange'
  })

  // Trigger validation when milestone resolver changes (after resolver is applied)
  useEffect(() => {
    milestonesForm.trigger()
  }, [milestoneFormResolver, milestonesForm])

  // Use field array for milestones
  const { append: _appendMilestone, remove: removeMilestone } = useFieldArray({
    control: milestonesForm.control,
    name: "milestones"
  })

  // Milestone append wrapper that automatically triggers validation for required field indicators
  const appendMilestone = useCallback((milestone: MilestoneFormData) => {
    const currentLength = milestonesForm.getValues('milestones').length
    _appendMilestone(milestone)
    // Trigger validation for the newly added milestone
    milestonesForm.trigger(`milestones.${currentLength}`)
  }, [milestonesForm, _appendMilestone])

  // Create conditional revenue recognition resolver - only validate when billing type is MILESTONE AND accounting fields are required
  const revenueRecognitionFormResolver = useMemo(() => {
    return (billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE && requireAccountingFields)
      ? zodResolver(createRevenueRecognitionFormSchema(
          totalAmount ?? undefined
        ))
      : undefined // No validation when not milestone billing or accounting fields not required
  }, [billingType, totalAmount, requireAccountingFields])

  // Create revenue recognition form
  const revenueRecognitionForm = useForm<RevenueRecognitionFormData>({
    defaultValues: {
      periods: {}
    },
    resolver: revenueRecognitionFormResolver,
    mode: 'onChange'
  })

  // Trigger validation when resolver changes (after resolver is applied)
  useEffect(() => {
    revenueRecognitionForm.trigger()
  }, [revenueRecognitionFormResolver, revenueRecognitionForm])

  // Watch revenue recognition periods for changes
  const revenueRecognitionPeriods = useWatch({
    control: revenueRecognitionForm.control,
    name: 'periods',
    defaultValue: {}
  })

  // Serialize period amounts for proper change detection
  const periodAmountsKey = useMemo(() => {
    return JSON.stringify(Object.values(revenueRecognitionPeriods).map(p => p?.amount))
  }, [revenueRecognitionPeriods])

  // Trigger validation when period amounts change
  useEffect(() => {
    revenueRecognitionForm.trigger('periods')
  }, [periodAmountsKey, revenueRecognitionForm])

  // Trigger validation when tasks resolver changes (after resolver is applied)
  useEffect(() => {
    tasksForm.trigger()
  }, [tasksFormResolver, tasksForm])

  // Use field array for tasks
  const { append: appendTaskToForm, remove: removeTaskFromForm, update: updateTaskInForm } = useFieldArray({
    control: tasksForm.control,
    name: "tasks"
  })

  // OPTIMIZED: Use useWatch for proper field array watching that detects individual field changes
  const milestones = useWatch({
    control: milestonesForm.control,
    name: 'milestones',
    defaultValue: []
  })

  // Helper function to calculate totals from milestones
  const calculateMilestoneTotals = useCallback((milestones: MilestoneFormData[]) => {
    const totalAmount = milestones.reduce((sum, m) => sum + (m.amount || 0), 0)
    const totalBudget = milestones.reduce((sum, m) => sum + (m.internalBudget || 0), 0)
    return {
      totalAmount: totalAmount,
      totalBudget: totalBudget
    }
  }, [])

  // Create a stable key for milestone totals to trigger recalculation when amounts/budgets change
  const milestoneTotalsKey = useMemo(() => {
    return milestones.map(m => `${m.amount}-${m.internalBudget}`).join('|')
  }, [milestones])

  // Helper function to create default tasks when editing.
  // Defaults to the current form's engagement/billing type, but accepts explicit values so callers
  // that set those types in the same tick (e.g. copyFromSource, which reset()s them from a source
  // engagement before the watched values update) can seed defaults for the SOURCE type rather than the
  // stale pre-change closure values.
  const createDefaultTasks = useCallback((
    taskEngagementType: string = engagementType,
    taskBillingType: string = billingType
  ): TaskFormData[] => {
    // For internal engagements or milestone billing, only create non-billable task
    const isMilestoneBilling = taskBillingType === ENGAGEMENT_BILLING_TYPE.MILESTONE
    const isBillableDisallowed = taskEngagementType === ENGAGEMENT_TYPE.INTERNAL || isMilestoneBilling

    if (isBillableDisallowed) {
      return [
        {
          id: 1,
          name: isMilestoneBilling ? "Utilized" : "Non-Billable",
          billable: false,
          inactive: false,
          ...(isMilestoneBilling && { isUtilized: true })
        }
      ]
    }
    
    // For other engagement types, create both billable and non-billable tasks
    return [
      {
        id: 1,
        name: "Billable",
        billable: true,
        inactive: false
      },
      {
        id: 2,
        name: "Non-Billable",
        billable: false,
        inactive: false
      }
    ]
  }, [engagementType, billingType])

  // Use engagement form validation with granular updates
  const { completedSections } = useEngagementFormValidation({
    core: coreForm.formState.isValid,
    billing: billingForm.formState.isValid,
    milestones: milestonesForm.formState.isValid,
    revenueRecognition: revenueRecognitionForm.formState.isValid,
    workers: workersForm.formState.isValid,
    timesheet: timesheetAndExpenseForm.formState.isValid,
    // Tasks: must be valid AND (if viewing required) user must have viewed the section
    tasks: tasksForm.formState.isValid && (!requireViewingForCompletion || viewedSections.has('tasks')),
    invoice: invoiceForm.formState.isValid && (!requireViewingForCompletion || viewedSections.has('invoice')),
    // Documents: optional - only mark complete once viewed if viewing is required
    documents: !requireViewingForCompletion || viewedSections.has('documents')
  })

  // Check if all sections are complete (memoized to prevent unnecessary recalculations)
  const allSectionsComplete = useMemo(() => {
    return Object.values(completedSections).every(Boolean)
  }, [completedSections])

  // Calculate which sections should be hidden based on engagement type
  const hiddenSections = useMemo(() => {
    return {
      invoice: engagementType === ENGAGEMENT_TYPE.INTERNAL, // Hide for internal engagements
    }
  }, [engagementType])

  // Populate form fields when engagement data loads
  useEffect(() => {
    if (formState) {
      // Mark forms as not populated while we're updating
      setIsFormsPopulated(false)

      // Mark as initial population before any form changes
      isInitialFormPopulation.current = true

      // Debounce form operations during rapid version changes
      const timeoutId = setTimeout(() => {
        // Reset forms (minimal operations only)
        coreForm.reset(formState.coreData)
        billingForm.reset(formState.billingData)
        workersForm.reset({ workers: formState.workers || [] })
        timesheetAndExpenseForm.reset(formState.timesheetData)
        invoiceForm.reset(formState.invoiceData)
        milestonesForm.reset({ milestones: formState.milestones || [] })
        revenueRecognitionForm.reset(formState.revenueRecognitionData)

        // Tasks - reset to saved values; default task creation is handled by a
        // separate effect that fires when engagementType first becomes non-empty
          tasksForm.reset({ tasks: formState.tasks || [] })
        
        // Sync prev refs to the loaded values so the change-detection effect
        // (which will fire on the next render after these resets) sees no diff
        // and does not create a timer that would clear dependent fields.
        prevSelectedInternalCompany.current = formState.coreData.selectedInternalCompany
        prevBillingType.current = formState.billingData.billingType
        prevEngagementType.current = formState.coreData.engagementType
        prevSelectedClient.current = formState.coreData.selectedClient

        // Safe to clear the flag now — any pre-existing change-detection timers
        // (from before this callback) already captured the flag as true. New
        // timers won't be created because the prev refs match the loaded values.
        isInitialFormPopulation.current = false

        // Trigger validation for all forms after population to ensure proper validation state
        Promise.all([
          coreForm.trigger(),
          billingForm.trigger(),
          workersForm.trigger(),
          timesheetAndExpenseForm.trigger(),
          invoiceForm.trigger(),
          milestonesForm.trigger(),
          revenueRecognitionForm.trigger(),
          tasksForm.trigger()
        ]).then(() => {
          // Mark forms as populated after all resets and validation complete
          setIsFormsPopulated(true)
        })
      }, 100) // Debounce form population

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [formState])

  // Memoize milestone totals calculation to avoid unnecessary recalculations
  const milestoneTotals = useMemo(() => {
    if (billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE) {
      return null
    }
    // For milestone billing, always calculate totals (even if zero when no milestones)
    return calculateMilestoneTotals(milestones)
  }, [billingType, milestoneTotalsKey, calculateMilestoneTotals])

  // Update billing form totals only when milestone totals change
  useEffect(() => {
    if (!milestoneTotals) return
    
    // Only update and trigger validation if values actually changed
    const currentTotalAmount = billingForm.getValues('totalAmount')
    const currentTotalBudget = billingForm.getValues('totalInternalBudget')
    
    if (currentTotalAmount !== milestoneTotals.totalAmount || currentTotalBudget !== milestoneTotals.totalBudget) {
      billingForm.setValue('totalAmount', milestoneTotals.totalAmount)
      billingForm.setValue('totalInternalBudget', milestoneTotals.totalBudget)
      
      // Trigger validation once for all changes
      billingForm.trigger(['totalAmount', 'totalInternalBudget'])
    }
  }, [milestoneTotals, billingForm])

  // Handle billing type changes immediately (no debounce for milestone creation)
  useEffect(() => {
    const hasBillingTypeChanged = prevBillingTypeForMilestones.current !== billingType
    
    if (hasBillingTypeChanged) {
      // Auto-create first milestone when switching to milestone billing (IMMEDIATE)
      if (billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE && milestones.length === 0) {
        const engagementStartDate = coreForm.getValues('startDate')
        const firstMilestone = createMilestoneWithDefaults(1, engagementStartDate)
        appendMilestone(firstMilestone)
      }
      
      prevBillingTypeForMilestones.current = billingType
    }
  }, [billingType, milestones.length, appendMilestone])

  // OPTIMIZED: Debounced effect to handle other form changes together
  useEffect(() => {
    const hasInternalCompanyChanged = prevSelectedInternalCompany.current !== selectedInternalCompany
    const hasBillingTypeChanged = prevBillingType.current !== billingType
    const hasEngagementTypeChanged = prevEngagementType.current !== engagementType

    if (hasInternalCompanyChanged || hasBillingTypeChanged || hasEngagementTypeChanged) {
      // Capture previous values before updating refs (needed for "switched away from" detection)
      const wasMilestoneBilling = prevBillingType.current === ENGAGEMENT_BILLING_TYPE.MILESTONE
      const wasInternalEngagement = prevEngagementType.current === ENGAGEMENT_TYPE.INTERNAL

      // Debounce form validation and field updates to prevent UI blocking
      const timer = setTimeout(() => {
        // Update refs first (always happens)
        prevSelectedInternalCompany.current = selectedInternalCompany
        prevBillingType.current = billingType
        prevEngagementType.current = engagementType

        // Skip all field clearing logic during initial form population
        if (isInitialFormPopulation.current) {
          isInitialFormPopulation.current = false
          return
        }

        console.log('🔄 Processing form changes:', {
          hasInternalCompanyChanged,
          hasBillingTypeChanged,
          hasEngagementTypeChanged
        })

        // Handle internal company changes
        if (hasInternalCompanyChanged) {
          coreForm.setValue('selectedSalesPerson', 0)
          coreForm.setValue('selectedEngagementLead', 0)
          coreForm.setValue('selectedClientExecutive', 0)
          timesheetAndExpenseForm.setValue('approvers', [])
          timesheetAndExpenseForm.setValue('expenseApprovers', [])
        }
        
        // Handle engagement type changes - enforce business rules
        if (hasEngagementTypeChanged) {
          // Business rule: Internal engagement type must use TIME_BASED billing
          if (engagementType === ENGAGEMENT_TYPE.INTERNAL && billingType !== ENGAGEMENT_BILLING_TYPE.TIME_BASED) {
            billingForm.setValue('billingType', ENGAGEMENT_BILLING_TYPE.TIME_BASED)
          }

          // Business rule: Internal engagements don't need client information
          if (engagementType === ENGAGEMENT_TYPE.INTERNAL) {
            coreForm.setValue('selectedClient', 0)
            coreForm.setValue('selectedContact', 0)
          }
        }
        
        // Business rule: reset default tasks and preserve custom tasks when type/billing changes.
        // Only runs in setup mode — engagement type and billing type are locked after first approval,
        // so this logic is not needed (and could corrupt saved project IDs) outside setup.
        // Default tasks (names in DEFAULT_TASK_NAMES) are replaced with the new defaults only when
        // any are present. Custom tasks are always kept but converted when billable is no longer
        // allowed. isUtilized mirrors billable: switching TO Milestone transfers billable → isUtilized
        // and clears billable; switching FROM Milestone restores billable ← isUtilized and clears it.
        if (canAutoManageTasks && (
            (hasEngagementTypeChanged && (engagementType === ENGAGEMENT_TYPE.INTERNAL || wasInternalEngagement)) ||
            (hasBillingTypeChanged && (billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE || wasMilestoneBilling)))) {
          const isBillableDisallowed = engagementType === ENGAGEMENT_TYPE.INTERNAL || billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE
          const isMilestone = billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE
          const currentTasks = tasksForm.getValues('tasks')
          const hasDefaultTasks = currentTasks.some(t => DEFAULT_TASK_NAMES.has(t.name))
          const nonDefaultTasks = currentTasks
            .filter(t => !DEFAULT_TASK_NAMES.has(t.name))
            .map(t => {
              if (isBillableDisallowed) {
                return isMilestone
                  ? { ...t, billable: false, isUtilized: t.billable }
                  : { ...t, billable: false, isUtilized: undefined }
              }
              if (wasMilestoneBilling) {
                return { ...t, billable: t.isUtilized ?? false, isUtilized: undefined }
              }
              return t
            })
          const newTasks = hasDefaultTasks
            ? [...createDefaultTasks(), ...nonDefaultTasks]
            : nonDefaultTasks
          const customTasks = currentTasks.filter(t => !DEFAULT_TASK_NAMES.has(t.name))
          const tasksChanged = hasDefaultTasks ||
            customTasks.some(t => isBillableDisallowed && t.billable) ||
            customTasks.some(t => wasMilestoneBilling && !isBillableDisallowed && t.isUtilized !== undefined)
          tasksForm.setValue('tasks', newTasks)
          tasksForm.trigger()
          if (tasksChanged) {
            unmarkSectionViewed('tasks')
            toast({
              title: 'Tasks updated',
              description: 'Your tasks have been updated to match your engagement settings. Please review before continuing.',
            })
          }
        }
        
        // OPTIMIZED: Single batch validation instead of multiple calls
        const formsToValidate = []
        if (hasInternalCompanyChanged) {
          formsToValidate.push(
            coreForm.trigger(['selectedSalesPerson', 'selectedEngagementLead', 'selectedClientExecutive']),
            timesheetAndExpenseForm.trigger(['approvers', 'expenseApprovers'])
          )
        }
      
        // Note: forms with conditional resolvers are handled by resolver change effects
        if (hasEngagementTypeChanged || hasBillingTypeChanged) {
          formsToValidate.push(
            coreForm.trigger(),
            billingForm.trigger()
          )
        }
        
        // Wait for all validations to complete
        Promise.all(formsToValidate).then(() => {
          console.log('✅ All form validations completed')
        })
      }, 50) // Reduced from 100ms to 50ms for better responsiveness
      
      return () => clearTimeout(timer)
    }
  }, [selectedInternalCompany, billingType, engagementType, canAutoManageTasks, coreForm, workersForm, timesheetAndExpenseForm, milestonesForm, tasksForm, createDefaultTasks, unmarkSectionViewed, toast])

  // Create default tasks when the form is first populated and no tasks exist.
  // Also re-fires when engagementType or billingType changes on a new engagement
  // where the user has not yet added any tasks.
  useEffect(() => {
    if (!isFormsPopulated || !canAutoManageTasks) return
    if (engagementType === '' || billingType === '') return
    if (tasksForm.getValues('tasks').length > 0) return
    tasksForm.setValue('tasks', createDefaultTasks())
    tasksForm.trigger()
    unmarkSectionViewed('tasks')
  }, [isFormsPopulated, engagementType, billingType, tasksForm, createDefaultTasks, canAutoManageTasks, unmarkSectionViewed])

  const { mainApi } = useApi()
  const prevSelectedClient = useRef<number>(selectedClient)

  useEffect(() => {
    // Advance the ref up front so it always tracks the current client — including on the change
    // path. If it were only updated when no change is detected, it would freeze at the loaded
    // client, and returning to a previously selected client would be mis-read as "no change",
    // skipping the resets and the sales-tax lookup below. (GW-562)
    const previousClient = prevSelectedClient.current
    prevSelectedClient.current = selectedClient

    // No-op on initial load / copy: the ref is pre-synced to the loaded client (see
    // apiToFormState / copyFromSource), so persisted values are not cleared there.
    if (previousClient === selectedClient) {
      return
    }

    let cancelled = false
    // Debounce client changes
    const timer = setTimeout(() => {
      // Reset contact when client changes
      coreForm.setValue('selectedContact', 0)

      // Reset invoice recipient and alternate bill when client changes
      invoiceForm.setValue('selectedInvoiceRecipient', 0)
      invoiceForm.setValue('selectedAlternateBill', null)

      // Default "Charge sales tax" from the newly selected client's OrganizationClientRole
      // .IsChargeSalesTax (endpoint defaults true when unset). `cancelled` prevents a stale
      // in-flight response from a superseded client from overwriting the current one. (GW-562)
      if (selectedClient) {
        mainApi.get<boolean>(`api/org/clientRoleChargeSalesTax/${selectedClient}`)
          .then((chargeSalesTax) => {
            if (!cancelled) billingForm.setValue('chargeSalesTax', chargeSalesTax ?? true)
          })
          .catch((error) => {
            console.error('Failed to load client charge-sales-tax default', error)
          })
      }

      // Trigger validation once for all changes
      Promise.all([
        coreForm.trigger(['selectedContact']),
        invoiceForm.trigger(['selectedInvoiceRecipient', 'selectedAlternateBill'])
      ]).then(() => {
        console.log('✅ Client change validations completed')
      })
    }, 50)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [selectedClient, coreForm, invoiceForm, billingForm, mainApi])

  // Resolve "Charge sales tax" from the client role when the engagement has no explicit value yet
  // (null — engagements created before the field existed, or bulk imports). This runs on load,
  // where the client-change effect above is intentionally suppressed, so the toggle reflects the
  // client's IsChargeSalesTax instead of a fabricated value — and a later save persists the real
  // value rather than silently flipping an exempt engagement to taxed. An explicit value (true/false)
  // is left untouched. (GW-562)
  useEffect(() => {
    if (!isFormsPopulated || !selectedClient) return
    if (billingForm.getValues('chargeSalesTax') !== null) return

    let cancelled = false
    mainApi.get<boolean>(`api/org/clientRoleChargeSalesTax/${selectedClient}`)
      .then((chargeSalesTax) => {
        if (!cancelled) billingForm.setValue('chargeSalesTax', chargeSalesTax ?? true)
      })
      .catch((error) => {
        console.error('Failed to resolve client charge-sales-tax default', error)
      })
    return () => { cancelled = true }
  }, [isFormsPopulated, selectedClient, billingForm, mainApi])

  // Milestone functions
  const handleMilestoneAdd = useCallback(() => {
    const currentMilestones = milestonesForm.getValues('milestones')
    const newId = currentMilestones.length > 0 ? Math.max(...currentMilestones.map(m => m.id)) + 1 : 1
    
    // Get previous milestone end date for start date calculation
    const previousMilestoneEndDate = currentMilestones.length > 0 
      ? currentMilestones[currentMilestones.length - 1].endDate 
      : null
    
    // Get engagement start date for first milestone
    const engagementStartDate = coreForm.getValues('startDate')
    
    const newMilestone = createMilestoneWithDefaults(newId, engagementStartDate, previousMilestoneEndDate)
    appendMilestone(newMilestone)
  }, [milestonesForm, appendMilestone, coreForm])

  const handleMilestoneRemove = useCallback((index: number) => {
    removeMilestone(index)
  }, [removeMilestone])

  // Worker functions
  const handleWorkerAdd = useCallback((worker: WorkerFormData) => {
    appendWorker(worker)
  }, [appendWorker])

  const handleWorkerEdit = useCallback((updatedWorker: WorkerFormData) => {
    const workers = workersForm.getValues('workers')
    const index = workers.findIndex(w => w.workerUserProfileId === updatedWorker.workerUserProfileId)
    if (index >= 0) {
      updateWorker(index, updatedWorker)
      // Trigger validation for the specific worker that was updated
      workersForm.trigger(`workers.${index}`)
    }
  }, [updateWorker, workersForm])

  const handleWorkerRemove = useCallback((workerUserProfileId: number) => {
    const workers = workersForm.getValues('workers')
    const index = workers.findIndex(w => w.workerUserProfileId === workerUserProfileId)
    if (index >= 0) {
      removeWorker(index)
    }
  }, [removeWorker, workersForm])

  // Task functions
  const handleTaskAdd = useCallback((task: TaskFormData) => {
    const isBillableDisallowed = billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE || engagementType === ENGAGEMENT_TYPE.INTERNAL
    if (isBillableDisallowed && task.billable) {
      return
    }
    appendTaskToForm(task)
  }, [appendTaskToForm, billingType, engagementType])

  const handleTaskEdit = useCallback((updatedTask: TaskFormData) => {
    const tasks = tasksForm.getValues('tasks')
    const index = tasks.findIndex(t => t.id === updatedTask.id)
    if (index >= 0) {
      updateTaskInForm(index, updatedTask)
      tasksForm.trigger(`tasks.${index}`)
    }
  }, [updateTaskInForm, tasksForm])

  const handleTaskRemove = useCallback((id: number) => {
    const tasks = tasksForm.getValues('tasks')
    const index = tasks.findIndex(t => t.id === id)
    if (index >= 0) {
      removeTaskFromForm(index)
    }
  }, [removeTaskFromForm, tasksForm])

  // Helper function to resequence approvers or expense approvers
  const resequence = (field: 'approvers' | 'expenseApprovers') => {
    const items = timesheetAndExpenseForm.getValues(field) || []
    items.forEach((_, idx) => {
      timesheetAndExpenseForm.setValue(`${field}.${idx}.sequence`, idx + 1)
    })
  }

  // Approver functions
  const handleApproverAdd = useCallback((userProfileId: number) => {
    const currentApprovers = timesheetAndExpenseForm.getValues('approvers')
    const newSequence = currentApprovers.length + 1
    appendApprover({
      userProfileId,
      sequence: newSequence
    })
  }, [appendApprover, timesheetAndExpenseForm])

  const handleApproverRemove = useCallback((index: number) => {
    removeApprover(index)
    // Update sequences for remaining approvers
    setTimeout(() => {
      resequence('approvers')
    }, 0)
  }, [removeApprover, timesheetAndExpenseForm])

  // Invoice note functions
  const handleInvoiceNoteAdd = useCallback(() => {
    appendInvoiceNote({ value: "" })
  }, [appendInvoiceNote])

  const handleInvoiceNoteRemove = useCallback((index: number) => {
    removeInvoiceNote(index)
  }, [removeInvoiceNote])

  // Expense Approver functions
  const handleExpenseApproverAdd = useCallback((userProfileId: number) => {
    const currentExpenseApprovers = timesheetAndExpenseForm.getValues('expenseApprovers') || []
    const newSequence = currentExpenseApprovers.length + 1
    appendExpenseApprover({
      userProfileId,
      sequence: newSequence
    })
  }, [appendExpenseApprover, timesheetAndExpenseForm])

  const handleExpenseApproverRemove = useCallback((index: number) => {
    removeExpenseApprover(index)
    // Update sequences for remaining expense approvers
    setTimeout(() => {
      resequence('expenseApprovers')
    }, 0)
  }, [removeExpenseApprover, timesheetAndExpenseForm])

  // Submit all forms to trigger validation and show errors
  const submitForms = useCallback(async () => {
    await Promise.all([
      coreForm.handleSubmit(() => {})(),
      billingForm.handleSubmit(() => {})(),
      workersForm.handleSubmit(() => {})(),
      timesheetAndExpenseForm.handleSubmit(() => {})(),
      invoiceForm.handleSubmit(() => {})(),
      milestonesForm.handleSubmit(() => {})(),
      revenueRecognitionForm.handleSubmit(() => {})(),
      tasksForm.handleSubmit(() => {})(),
    ])
  }, [coreForm, billingForm, workersForm, timesheetAndExpenseForm, invoiceForm, milestonesForm, revenueRecognitionForm, tasksForm])

  // Copy the listed field subset from a source engagement's FormState into the current forms (GW-198).
  // Routed through this hook (not the page) so it can reuse the same clobber-suppression the initial
  // population effect uses: set isInitialFormPopulation.current = true and pre-sync the prev* refs, so the
  // debounced change-detection effects (internal-company/client/billing-type clearing, milestone
  // auto-create, task auto-manage) see no diff and don't wipe the just-copied dependent fields.
  const copyFromSource = useCallback((source: FormState) => {
    isInitialFormPopulation.current = true

    // Core: overlay only the copied subset; preserve name/code/dates/requestType/originalEngagement.
    const currentCore = coreForm.getValues()
    coreForm.reset({
      ...currentCore,
      engagementType: source.coreData.engagementType,
      selectedClient: source.coreData.selectedClient,
      selectedContact: source.coreData.selectedContact,
      selectedInternalCompany: source.coreData.selectedInternalCompany,
      selectedBranch: source.coreData.selectedBranch,
      selectedSalesPerson: source.coreData.selectedSalesPerson,
      selectedEngagementLead: source.coreData.selectedEngagementLead,
      selectedProjectLead: source.coreData.selectedProjectLead,
      selectedClientExecutive: source.coreData.selectedClientExecutive,
      isPreEngagement: source.coreData.isPreEngagement,
    })

    // Milestones are not copied: the source's milestones describe its own term, and a copied reference
    // would match the source's milestone transactions. They start as on a new engagement, which for
    // Milestone billing is the single default milestone the billing-type effect would auto-create.
    const isMilestoneBilling = source.billingData.billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE
    const initialMilestones = isMilestoneBilling
      ? [createMilestoneWithDefaults(1, coreForm.getValues('startDate'))]
      : getInitialMilestones()

    // Milestone billing derives its totals from the milestones, so the source's totals are replaced
    // here; the totals effect only reruns when the milestone amounts change, which a blank-to-blank
    // reset does not.
    const initialMilestoneTotals = calculateMilestoneTotals(initialMilestones)
    billingForm.reset(isMilestoneBilling
      ? { ...source.billingData, totalAmount: initialMilestoneTotals.totalAmount, totalInternalBudget: initialMilestoneTotals.totalBudget }
      : source.billingData)
    // Workers: copy only the source's ACTIVE workers (apiToFormState merges removed/terminated workers
    // into the same array with isRemoved:true — those must not carry into a brand-new CR). Each copied
    // worker is genuinely new on this engagement, so mark isNew and drop the source's legacy-sync status.
    const engagementStartDate = coreForm.getValues('startDate')
    const copiedWorkers = (source.workers || [])
      .filter(worker => !worker.isRemoved)
      .map(worker => ({
        ...worker,
        isRemoved: false,
        isNew: true,
        legacySyncStatus: undefined,
        legacySyncError: undefined,
        // A copied worker carries no extension dates, the same as a newly added one.
        originalStartDate: null,
        extensionStartDate: null,
        extensionEndDate: null,
        // Only the changes still to come follow the worker, as new entries. They are checked against the
        // re-based dates below on save, so one that no longer fits is flagged for the reader to fix.
        scheduledBudgetedHours: copyPendingEntries(worker.scheduledBudgetedHours),
        ...copiedWorkerDates(worker, engagementStartDate),
      }))
    workersForm.reset({ workers: copiedWorkers })
    // A real (live) source always has >=1 task — the "at least one active task" rule is enforced when
    // it was saved/approved — so source.tasks is normally non-empty. Guard the degenerate empty case:
    // in setup mode seed the type-appropriate defaults, otherwise the new CR would fail task validation
    // with no way to recover, because the default-task effect keys off engagementType/billingType (not
    // the task array) and won't fire after a same-type copy.
    const copiedTasks = source.tasks?.length
      ? source.tasks
      : (canAutoManageTasks ? createDefaultTasks(source.coreData.engagementType, source.billingData.billingType) : [])
    tasksForm.reset({ tasks: copiedTasks })
    milestonesForm.reset({ milestones: initialMilestones })
    timesheetAndExpenseForm.reset(source.timesheetData)
    // Invoice setup copies EXCEPT notes (per PRD); revenue recognition is intentionally not copied.
    invoiceForm.reset({ ...source.invoiceData, invoiceNotes: [] })
    // Revenue recognition is not copied, but it must be CLEARED (not left as-is): we just overwrote
    // billing/milestones, so any periods the user entered before copying would otherwise be saved
    // against the freshly-copied setup.
    revenueRecognitionForm.reset({ periods: {} })

    // Sync prev refs to the just-applied values so the change-detection effects don't clear/auto-create.
    prevSelectedInternalCompany.current = source.coreData.selectedInternalCompany
    prevSelectedClient.current = source.coreData.selectedClient
    prevEngagementType.current = source.coreData.engagementType
    prevBillingType.current = source.billingData.billingType
    prevBillingTypeForMilestones.current = source.billingData.billingType

    isInitialFormPopulation.current = false

    void Promise.all([
      coreForm.trigger(),
      billingForm.trigger(),
      workersForm.trigger(),
      timesheetAndExpenseForm.trigger(),
      invoiceForm.trigger(),
      milestonesForm.trigger(),
      revenueRecognitionForm.trigger(),
      tasksForm.trigger(),
    ])
  }, [coreForm, billingForm, workersForm, tasksForm, milestonesForm, timesheetAndExpenseForm, invoiceForm, revenueRecognitionForm, canAutoManageTasks, createDefaultTasks, calculateMilestoneTotals])

  // Helper function to build current form state (memoized to prevent expensive recalculations)
  const buildCurrentFormState = useCallback(() => {
    return {
      // Core section includes client data
      coreData: coreForm.getValues(),
      
      // Billing section
      billingData: billingForm.getValues(),
      
      // Workers section
      workers: workersForm.getValues('workers'),
      
      // Timesheet section
      timesheetData: timesheetAndExpenseForm.getValues(),
      
      // Tasks section
      tasks: tasksForm.getValues('tasks'),
      
      // Invoice section
      invoiceData: invoiceForm.getValues(),
      
      // Milestones
      milestones: milestonesForm.getValues('milestones'),
      
      // Revenue Recognition
      revenueRecognitionData: revenueRecognitionForm.getValues(),
      
      // System metadata
      lastModifiedDateTime: formState?.lastModifiedDateTime || null,
      
      // Action context (from loaded engagement or default)
      actionContext: formState?.actionContext || {
        engagement: {} as any,
        availableActions: {},
        canUpdate: false,
        canDelete: false,
        canSave: false,
        canSubmit: false,
        canApprove: false,
        canDecline: false,
        canDiscard: false,
        canCreateChangeRequest: false,
        canGenerateMilestoneTransaction: false,
        canSubmitForReview: false,
        canRecall: false,
        canRecallToAccounting: false,
        canComplete: false,
        canReactivate: false,
        canLockRevenueRecognition: false,
        canViewWorkerPayRates: false,
        canEditEngagementSource: false,
        references: { profiles: {}, organizations: {}, engagements: {} }
      }
    }
  }, [coreForm, billingForm, workersForm, timesheetAndExpenseForm, tasksForm, invoiceForm, milestonesForm, revenueRecognitionForm, formState])

  return {
    // Form instances
    forms: {
      coreForm,
      billingForm,
      workersForm,
      timesheetAndExpenseForm,
      invoiceForm,
      milestonesForm,
      revenueRecognitionForm,
      tasksForm
    },
    
    // Handlers
    handlers: {
      handleMilestoneAdd,
      handleMilestoneRemove,
      handleWorkerAdd,
      handleWorkerEdit,
      handleWorkerRemove,
      handleTaskAdd,
      handleTaskEdit,
      handleTaskRemove,
      handleApproverAdd,
      handleApproverRemove,
      handleInvoiceNoteAdd,
      handleInvoiceNoteRemove,
      handleExpenseApproverAdd,
      handleExpenseApproverRemove
    },
    
    // State
    state: {
      completedSections,
      hiddenSections,
      allSectionsComplete,
      milestoneTotals,
      billingType,
      engagementType,
      selectedClient,
      isFormsPopulated,
      taskCoverage
    },
    
    // Actions
    actions: {
      buildCurrentFormState,
      submitForms,
      copyFromSource
    }
  }
} 